/**
 * PortfolioContextBar — the portfolio of a completed analysis, as a compact
 * horizontal bar instead of a permanent 380/420px sidebar column.
 *
 * Phase 5 follow-up: in the Results the data is the subject, the portfolio is
 * the context. Compact by default (name, count, tickers, download); expanded on
 * request (full ETF list, add, remove, saved portfolios, new analysis). Every
 * handler and every `data-testid` comes from the old sidebar: no portfolio
 * logic lives here.
 */
import { useState } from "react";
import { ChevronDown, ChevronRight, Download, FileText, Plus, Star, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

function EtfRow({ etf, onRemove, disabled }) {
  return (
    <div
      className="flex items-center justify-between gap-2 rounded border border-line bg-surface-subtle/40 px-2 py-1.5 text-xs min-w-0"
      data-testid={`compact-queue-row-${etf.isin}`}
    >
      <div className="flex min-w-0 items-center gap-2">
        <Star className="h-3.5 w-3.5 flex-shrink-0 text-brand-accent" aria-hidden="true" />
        <div className="min-w-0">
          <div className="break-words font-medium text-content">{etf.etf_name || etf.isin}</div>
          <div className="break-words text-label text-content-muted">
            {etf.ticker ? `${etf.ticker} · ` : ""}{etf.isin}
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={() => onRemove(etf.isin)}
        disabled={disabled}
        data-testid={`etf-context-remove-${etf.isin}`}
        className="flex items-center justify-center shrink-0 w-10 h-10 -my-2 -mr-2 rounded text-content-muted hover:bg-surface-subtle hover:text-danger disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
        aria-label={`Rimuovi ${etf.etf_name || etf.isin}`}
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}

export function PortfolioContextBar({
  selectedEtfs = [],
  sessionId,
  downloadExcel,
  onRemoveEtf,
  onReset,
  onOpenAddMore,
  portfolioActions = null,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  // Phase 6 — the ticker summary must fit a 390px row: show a few, count the rest.
  const tickers = selectedEtfs.map((e) => e.ticker || e.isin);
  const inline = tickers.slice(0, 3).join(" · ");
  const rest = tickers.length - 3;
  const summary = tickers.length === 0
    ? "Nessun ETF nel portafoglio"
    : rest > 0 ? `${inline} · +${rest}` : inline;

  return (
    <section
      data-testid="portfolio-context-bar"
      className="rounded-card border border-line-soft bg-surface shadow-card px-3 py-2"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <button
          type="button"
          data-testid="portfolio-context-toggle"
          aria-expanded={open}
          aria-controls="portfolio-context-details"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-2 min-h-10 px-2 -mx-2 rounded-md text-sm text-content
                     hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
        >
          {open
            ? <ChevronDown className="h-4 w-4 text-content-muted" aria-hidden="true" />
            : <ChevronRight className="h-4 w-4 text-content-muted" aria-hidden="true" />}
          <FileText className="h-4 w-4 text-content-muted" aria-hidden="true" />
          <span className="font-semibold">Portafoglio</span>
          <Badge className="bg-brand/20 text-brand-accent text-label">{selectedEtfs.length}</Badge>
          <span className="text-label text-content-muted">
            {open ? "Nascondi dettagli" : "Mostra dettagli"}
          </span>
        </button>

        <p
          data-testid="portfolio-context-tickers"
          className="min-w-0 flex-1 truncate text-xs text-content-muted"
          title={tickers.length > 3 ? tickers.join(" · ") : undefined}
        >
          {summary}
        </p>

        <Button
          data-testid="download-excel-btn"
          onClick={downloadExcel}
          disabled={!sessionId || disabled}
          className="h-10 w-full sm:w-auto shrink-0 bg-brand hover:bg-brand-hover font-medium text-content-on-brand"
        >
          <Download className="mr-2 h-4 w-4" aria-hidden="true" />
          Scarica Excel
        </Button>
      </div>

      {open && (
        <div
          id="portfolio-context-details"
          data-testid="portfolio-context-details"
          className="mt-3 pt-3 border-t border-line-soft grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-4
                     animate-in fade-in-0 slide-in-from-top-1 duration-base ease-system"
        >
          <div className="space-y-1.5 min-w-0">
            {selectedEtfs.length === 0 ? (
              <p className="text-xs text-content-muted">Nessun ETF nel portafoglio.</p>
            ) : (
              selectedEtfs.map((etf) => (
                <EtfRow key={etf.isin} etf={etf} onRemove={onRemoveEtf} disabled={disabled} />
              ))
            )}
          </div>

          <div className="space-y-2">
            {onOpenAddMore && (
              <Button
                variant="outline"
                data-testid="workspace-add-more"
                onClick={onOpenAddMore}
                disabled={disabled}
                className="h-10 w-full justify-start border-line text-content-secondary hover:bg-surface-subtle"
              >
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Aggiungi ETF
              </Button>
            )}
            {portfolioActions}
            <Button
              data-testid="workspace-restart"
              onClick={onReset}
              variant="outline"
              disabled={disabled}
              className="h-10 w-full justify-start border-line text-content-secondary hover:bg-surface-subtle"
            >
              <ChevronRight className="mr-2 h-4 w-4 rotate-180" aria-hidden="true" />
              Nuova analisi
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

export default PortfolioContextBar;
